import React, { useState, useMemo, useRef, useEffect } from 'react';
import Swal from 'sweetalert2';
import { 
    FaEdit, FaTrashAlt, FaTimes, FaUpload, 
    FaChevronDown, FaChevronUp, FaPlus, FaExchangeAlt, FaFilePdf 
} from 'react-icons/fa';
import { Helmet } from "react-helmet-async";
import './PratibhaKhojForm.css';
import axiosInstance from '../../api/axiosInstance'; // Make sure path is correct

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://localhost:5000";

// --- MODAL COMPONENT ---
const FormModal = ({ isOpen, onClose, formDoc, mode, onSave }) => {
    const [formData, setFormData] = useState({ isActive: true });
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            if (formDoc) {
                setFormData(formDoc);
                setPreviewUrl(formDoc.fileUrl ? `${API_BASE_URL}${formDoc.fileUrl}` : '');
                setFile(null);
            } else {
                setFormData({ isActive: true });
                setPreviewUrl('');
                setFile(null);
            }
        }
    }, [formDoc, isOpen]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            const ext = selectedFile.name.split('.').pop().toLowerCase();
            if(!['pdf', 'png', 'jpg', 'jpeg'].includes(ext)) {
                return Swal.fire('Invalid File', 'Only PDF, PNG, JPG, and JPEG are allowed.', 'error');
            }
            setFile(selectedFile);
            setPreviewUrl(URL.createObjectURL(selectedFile));
        }
    };

    const handleRemoveMedia = (e) => {
        e.stopPropagation();
        setFile(null);
        setPreviewUrl('');
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSubmit = () => {
        if (mode === 'add' && !file) return Swal.fire('Error', 'Please upload a PDF or Image file', 'error');
        
        const submission = new FormData();
        // In Add mode, backend handles forcing isActive to true. In edit mode, admin might toggle.
        if (mode === 'edit') {
            submission.append('isActive', formData.isActive);
        }
        if (file) submission.append('file', file);

        onSave(submission);
    };

    const isPdf = file ? file.type === 'application/pdf' : previewUrl.toLowerCase().includes('.pdf');
    const isView = mode === 'view';

    if (!isOpen) return null;

    return (
        <div className="admin-pkf-modal-overlay">
            <div className="admin-pkf-modal-content" onClick={e => e.stopPropagation()}>
                <div className="admin-pkf-modal-header">
                    <h2>{isView ? 'View Form Details' : mode === 'edit' ? 'Edit Form' : 'Upload New Form'}</h2>
                    <button className="admin-pkf-modal-close-btn" onClick={onClose}><FaTimes /></button>
                </div>
                <div className="admin-pkf-modal-body">
                    {mode === 'add' && (
                        <div className="admin-pkf-info-box">
                            <p><strong>Note:</strong> Uploading a new form will automatically make it Active and disable all previous forms.</p>
                        </div>
                    )}

                    <div className="admin-pkf-preview-container" onClick={() => !isView && fileInputRef.current.click()}>
                        {previewUrl ? (
                            <div className="admin-pkf-media-display">
                                {isPdf ? (
                                    <div className="admin-pkf-pdf-preview"><FaFilePdf className="admin-pkf-pdf-icon" /> <p>PDF Uploaded</p></div>
                                ) : (
                                    <img src={previewUrl} alt="Preview" onError={(e) => e.target.src='https://via.placeholder.com/300?text=Error'} />
                                )}
                                {!isView && (
                                    <div className="admin-pkf-preview-overlay">
                                        <button className="admin-pkf-overlay-btn replace"><FaExchangeAlt /> Replace</button>
                                        <button className="admin-pkf-overlay-btn remove" onClick={handleRemoveMedia}><FaTrashAlt /> Remove</button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="admin-pkf-upload-prompt">
                                <FaUpload className="admin-pkf-upload-icon"/>
                                <span>Click to Upload PDF or Image (JPG, PNG)</span>
                            </div>
                        )}
                        <input type="file" ref={fileInputRef} hidden onChange={handleFileChange} accept=".pdf, .png, .jpg, .jpeg" />
                    </div>

                    {mode === 'edit' && !isView && (
                        <div className="admin-pkf-form-group-checkbox">
                            <label className="admin-pkf-switch">
                                <input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({...formData, isActive: e.target.checked})} />
                                <span className="admin-pkf-slider-toggle" />
                            </label>
                            <span className="admin-pkf-checkbox-text">Set as Active Form (This will disable all other forms)</span>
                        </div>
                    )}
                </div>
                <div className="admin-pkf-modal-footer">
                    <button className="admin-pkf-btn-cancel" onClick={onClose}>{isView ? 'Close' : 'Cancel'}</button>
                    {!isView && <button className="admin-pkf-btn-save" onClick={handleSubmit}>{mode === 'add' ? 'Upload & Activate' : 'Save Changes'}</button>}
                </div>
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---
const PratibhaKhojForm = () => {
    const [forms, setForms] = useState([]);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); 
    const [selectedForm, setSelectedForm] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);

    const toggleRow = (id) => setExpandedRow(prev => (prev === id ? null : id));
    const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

    const fetchForms = async () => {
        try {
            const res = await axiosInstance.get('/api/pratibha-khoj-forms');
            setForms(res.data);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => { fetchForms(); }, []);

    const totalResults = forms.length;
    const totalPages = Math.ceil(totalResults / itemsPerPage);
    const startResult = totalResults === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endResult = Math.min(currentPage * itemsPerPage, totalResults);
    
    const paginatedForms = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return forms.slice(startIndex, startIndex + itemsPerPage);
    }, [forms, currentPage, itemsPerPage]);

    const handleSave = async (formData) => {
        const url = modalMode === 'edit' ? `/api/pratibha-khoj-forms/${selectedForm._id}` : `/api/pratibha-khoj-forms`;
        const method = modalMode === 'edit' ? 'put' : 'post';

        try {
            await axiosInstance({ method, url, data: formData });
            await fetchForms();
            Swal.fire(modalMode === 'edit' ? 'Updated!' : 'Added & Activated!', '', 'success');
            setIsModalOpen(false);
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Operation failed', 'error');
        }
    };

    const handleDelete = (id) => {
        Swal.fire({ title: 'Delete Form?', text: 'Cannot be undone.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e74c3c' }).then(async res => {
            if (res.isConfirmed) {
                try {
                    await axiosInstance.delete(`/api/pratibha-khoj-forms/${id}`);
                    await fetchForms();
                    Swal.fire('Deleted', '', 'success');
                } catch (error) {
                    Swal.fire('Error', error.response?.data?.message || 'Delete failed', 'error');
                }
            }
        });
    };

    const toggleActive = async (id, currentStatus) => {
        const newStatus = !currentStatus;
        if(newStatus === true) {
            Swal.fire({
                title: 'Make Active?',
                text: 'This will instantly deactivate all other forms. Continue?',
                icon: 'info',
                showCancelButton: true,
                confirmButtonColor: '#3498db'
            }).then(async res => {
                if (res.isConfirmed) {
                    await processToggle(id, newStatus);
                }
            });
        } else {
            await processToggle(id, newStatus);
        }
    };

    const processToggle = async (id, newStatus) => {
        try {
            await axiosInstance.put(`/api/pratibha-khoj-forms/${id}`, { isActive: newStatus });
            await fetchForms();
        } catch (error) {
            Swal.fire('Error', 'Failed to toggle status', 'error');
        }
    }

    const openModal = (mode, formDoc = null) => {
        setModalMode(mode);
        setSelectedForm(formDoc);
        setIsModalOpen(true);
    };

    return (
        <>
        <Helmet>
                    <title>Pratibha Khoj Form | Rajput Shiksha Kosh</title>
            
                    <meta
                      name="description"
                      content="Manage pratibha khoj forms for the Rajput Shiksha Kosh organization."
                    />
                  </Helmet>
        <div className="admin-pkf-wrapper">
            <div className="admin-pkf-container">
                <FormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} formDoc={selectedForm} mode={modalMode} onSave={handleSave} />
                
                <h2 className="admin-pkf-title">Pratibha Khoj Form Management</h2>

                <div className="admin-pkf-controls">
                    <div className="admin-pkf-action-group" style={{ width: '100%', justifyContent: 'space-between' }}>
                        <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="admin-pkf-limit-select">
                            <option value="10">10 / page</option>
                            <option value="20">20 / page</option>
                        </select>
                        <button className="admin-pkf-add-btn" onClick={() => openModal('add')}>
                            <FaPlus /> Upload New Form
                        </button>
                    </div>
                </div>

                <div className="admin-pkf-table-card">
                    <div className="admin-pkf-table-wrapper">
                        <table className="admin-pkf-table">
                            <thead>
                                <tr>
                                    <th className="admin-pkf-col-sno">S.No</th>
                                    <th className="admin-pkf-col-media">File Preview</th>
                                    <th className="admin-pkf-col-date">Uploaded At</th>
                                    <th className="admin-pkf-col-active">Status</th>
                                    <th className="admin-pkf-col-actions">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedForms.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="admin-pkf-no-data">No forms found. Click "Upload New Form".</td>
                                    </tr>
                                ) : (
                                    paginatedForms.map((formDoc, index) => {
                                        const isPdf = formDoc.fileType === '.pdf' || formDoc.fileUrl.toLowerCase().includes('.pdf');
                                        return (
                                        <React.Fragment key={formDoc._id}>
                                            <tr className="admin-pkf-row">
                                                <td className="admin-pkf-col-sno">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                                <td className="admin-pkf-col-media">
                                                    <div className="admin-pkf-media-wrapper">
                                                        <div className="admin-pkf-thumbnail" onClick={() => openModal('view', formDoc)}>
                                                            {isPdf ? (
                                                                <FaFilePdf className="admin-pkf-pdf-icon-sm" />
                                                            ) : (
                                                                <img src={`${API_BASE_URL}${formDoc.fileUrl}`} alt="Form" onError={(e) => e.target.src='https://via.placeholder.com/80?text=Err'}/>
                                                            )}
                                                        </div>
                                                        <button className="admin-pkf-mobile-toggle-btn" onClick={() => toggleRow(formDoc._id)}>
                                                            {expandedRow === formDoc._id ? <FaChevronUp /> : <FaChevronDown />}
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="admin-pkf-col-date">{formatDate(formDoc.createdAt)}</td>
                                                <td className="admin-pkf-col-active">
                                                    <label className="admin-pkf-switch">
                                                        <input type="checkbox" checked={formDoc.isActive === true} onChange={() => toggleActive(formDoc._id, formDoc.isActive)} />
                                                        <span className="admin-pkf-slider-toggle" />
                                                    </label>
                                                    <div className={`admin-pkf-status-text ${formDoc.isActive ? 'active' : 'disabled'}`}>
                                                        {formDoc.isActive ? 'Active' : 'Disabled'}
                                                    </div>
                                                </td>
                                                <td className="admin-pkf-col-actions">
                                                    <div className="admin-pkf-action-icons">
                                                        <button className="admin-pkf-icon-btn edit" onClick={() => openModal('edit', formDoc)} title="Edit"><FaEdit /></button>
                                                        <button className="admin-pkf-icon-btn delete" onClick={() => handleDelete(formDoc._id)} title="Delete"><FaTrashAlt /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {expandedRow === formDoc._id && (
                                                <tr className="admin-pkf-details-row">
                                                    <td colSpan="5">
                                                        <div className="admin-pkf-details-content">
                                                            <div className="admin-pkf-details-grid">
                                                                <div className="admin-pkf-detail-card admin-pkf-dc-date-created">
                                                                    <div className="admin-pkf-detail-label">Uploaded At</div>
                                                                    <div className="admin-pkf-detail-value">{formatDate(formDoc.createdAt)}</div>
                                                                </div>
                                                                <div className="admin-pkf-detail-card admin-pkf-dc-active">
                                                                    <div className="admin-pkf-detail-label">Status</div>
                                                                    <label className="admin-pkf-switch">
                                                                        <input type="checkbox" checked={formDoc.isActive === true} onChange={() => toggleActive(formDoc._id, formDoc.isActive)} />
                                                                        <span className="admin-pkf-slider-toggle" />
                                                                    </label>
                                                                </div>
                                                                <div className="admin-pkf-detail-card admin-pkf-dc-actions">
                                                                    <div className="admin-pkf-detail-label">Actions</div>
                                                                    <div className="admin-pkf-action-icons" style={{ justifyContent: 'flex-start' }}>
                                                                        <button className="admin-pkf-icon-btn edit" onClick={() => openModal('edit', formDoc)}><FaEdit /> Edit</button>
                                                                        <button className="admin-pkf-icon-btn delete" onClick={() => handleDelete(formDoc._id)}><FaTrashAlt /> Delete</button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    )})
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                
                {paginatedForms.length > 0 && (
                    <div className="admin-pkf-pagination-container">
                        <div className="admin-pkf-pagination-info">Showing {startResult} to {endResult} of {totalResults} forms</div>
                        <div className="admin-pkf-pagination-controls">
                            <button className="admin-pkf-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Previous</button>
                            <span className="admin-pkf-page-count">Page {currentPage} of {totalPages || 1}</span>
                            <button className="admin-pkf-page-btn" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
        </>
    );
};

export default PratibhaKhojForm;